import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  Divider,
  InputAdornment,
  Alert,
  Switch,
  FormControlLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Grid,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { formatInputWithSeparators, handleNumericInputChange } from "../../utils/numeros.js";

// ----- Transfer List -----
function TransferList({ disponibles, seleccionados, onChange }) {
  const handleMoveRight = (item) => {
    onChange(disponibles.filter((a) => a.id !== item.id), [...seleccionados, item]);
  };

  const handleMoveLeft = (item) => {
    onChange([...disponibles, item], seleccionados.filter((a) => a.id !== item.id));
  };

  const handleAllRight = () => onChange([], [...seleccionados, ...disponibles]);
  const handleAllLeft = () => onChange([...disponibles, ...seleccionados], []);

  const customList = (title, items, onItemClick) => (
    <Paper variant="outlined" sx={{ width: "100%", minHeight: 140, maxHeight: 200, overflow: "auto" }}>
      <Box sx={{ px: 1.5, py: 0.75, borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {title} ({items.length})
        </Typography>
      </Box>
      <List dense disablePadding>
        {items.length === 0 ? (
          <ListItem>
            <ListItemText primary={<Typography variant="caption" color="text.disabled">Sin elementos</Typography>} />
          </ListItem>
        ) : (
          items.map((item) => (
            <ListItem key={item.id} dense button onClick={() => onItemClick(item)} sx={{ py: 0.5, cursor: "pointer" }}>
              <ListItemText primary={<Typography variant="body2">{item.label}</Typography>} />
            </ListItem>
          ))
        )}
      </List>
    </Paper>
  );

  return (
    <Grid container spacing={1.5} alignItems="center">
      <Grid item xs={5}>{customList("Disponibles", disponibles, handleMoveRight)}</Grid>
      <Grid item xs={2}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
          <Button size="small" variant="outlined" onClick={handleAllRight} disabled={disponibles.length === 0} sx={{ minWidth: 40, px: 0.5, fontSize: 14 }}>≫</Button>
          <Button size="small" variant="outlined" onClick={handleAllLeft} disabled={seleccionados.length === 0} sx={{ minWidth: 40, px: 0.5, fontSize: 14 }}>≪</Button>
        </Box>
      </Grid>
      <Grid item xs={5}>{customList("Seleccionados", seleccionados, handleMoveLeft)}</Grid>
    </Grid>
  );
}

// ----- Modal principal -----
const ModalEditarPrecioId = ({
  open,
  handleClose,
  precioLista,
  onUpdate,
  onUpdateMargen,
  onPromoverReferente,
  onActualizarPreciosClientes,
  getClientesConPreciosByFiltros,
  localeConfig,
}) => {
  const locale = localeConfig?.locale ?? "es-AR";
  const [nuevoMonto, setNuevoMonto] = useState("");
  const [tipoAjuste, setTipoAjuste] = useState("valor");
  const [nuevoMargen, setNuevoMargen] = useState("");

  const [promoverReferente, setPromoverReferente] = useState(false);
  const [actualizarPreciosClientes, setActualizarPreciosClientes] = useState(false);
  const [clientesDisponibles, setClientesDisponibles] = useState([]);
  const [clientesSeleccionados, setClientesSeleccionados] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const esReferente = precioLista?.esProveedorReferente ?? true;

  useEffect(() => {
    if (precioLista && open) {
      setNuevoMonto(precioLista.costo ?? "");
      setNuevoMargen(precioLista.margenGanancia != null ? String(precioLista.margenGanancia) : "");
      const esRef = precioLista.esProveedorReferente ?? true;
      setPromoverReferente(false);
      setError(null);
      if (esRef) {
        setActualizarPreciosClientes(true);
        setLoadingClientes(true);
        getClientesConPreciosByFiltros?.({ idItem: precioLista.idItem, idUbicacion: precioLista.idUbicacion })
          .then((lista) => {
            setClientesDisponibles(lista || []);
            setClientesSeleccionados([]);
          })
          .finally(() => setLoadingClientes(false));
      } else {
        setActualizarPreciosClientes(false);
        setClientesDisponibles([]);
        setClientesSeleccionados([]);
      }
    }
  }, [precioLista, open, getClientesConPreciosByFiltros]);

  const cargarClientes = useCallback(async () => {
    if (!getClientesConPreciosByFiltros || !precioLista) return;
    setLoadingClientes(true);
    try {
      const lista = await getClientesConPreciosByFiltros({
        idItem: precioLista.idItem,
        idUbicacion: precioLista.idUbicacion,
      });
      setClientesDisponibles(lista || []);
      setClientesSeleccionados([]);
    } finally {
      setLoadingClientes(false);
    }
  }, [getClientesConPreciosByFiltros, precioLista]);

  const handleTogglePromover = (checked) => {
    setPromoverReferente(checked);
    if (checked) cargarClientes();
  };

  const handleToggleActualizarPrecios = (checked) => {
    setActualizarPreciosClientes(checked);
    if (checked) cargarClientes();
  };

  const handleGuardar = async () => {
    setError(null);
    setSaving(true);
    try {
      const montoNum = typeof nuevoMonto === "number" ? nuevoMonto : parseFloat(nuevoMonto);
      const margenNum = (nuevoMargen !== "" && nuevoMargen != null) ? (typeof nuevoMargen === "number" ? nuevoMargen : parseFloat(nuevoMargen)) : null;
      if (promoverReferente && !esReferente) {
        await onPromoverReferente(
          precioLista.idItem,
          precioLista.idEntidad,
          precioLista.idUbicacion,
          montoNum,
          tipoAjuste,
          margenNum != null && !isNaN(margenNum) ? margenNum : null,
          clientesSeleccionados.map((c) => c.id),
        );
      } else {
        await onUpdate(precioLista.idItem, precioLista.idEntidad, precioLista.idUbicacion, montoNum, tipoAjuste);
        if (onUpdateMargen && margenNum != null && !isNaN(margenNum)) {
          await onUpdateMargen(precioLista.idItem, precioLista.idEntidad, precioLista.idUbicacion, margenNum);
        }
        if (actualizarPreciosClientes && onActualizarPreciosClientes && clientesSeleccionados.length > 0) {
          await onActualizarPreciosClientes(
            precioLista.idItem,
            precioLista.idEntidad,
            precioLista.idUbicacion,
            clientesSeleccionados.map((c) => c.id),
          );
        }
      }
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Ocurrió un error al guardar los cambios.");
    } finally {
      setSaving(false);
    }
  };

  const mostrarTransferList = (esReferente && actualizarPreciosClientes) || (!esReferente && promoverReferente);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 680,
          maxHeight: "92vh",
          borderRadius: 2,
          boxShadow: 24,
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1.5 }}>
        <Typography variant="h6" component="span">
          Actualizar costo y margen
        </Typography>
        <IconButton size="small" onClick={handleClose} aria-label="cerrar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Sección Costo */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: "100%", borderColor: "secondary.light", bgcolor: "rgba(255,152,0,0.02)" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "secondary.main", mb: 2 }}>
                Costo
              </Typography>
              <Select
                value={tipoAjuste}
                onChange={(e) => setTipoAjuste(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
              >
                <MenuItem value="valor">Actualizar por valor</MenuItem>
                <MenuItem value="porcentaje">Actualizar por porcentaje</MenuItem>
              </Select>
              <TextField
                label={tipoAjuste === "porcentaje" ? "Porcentaje (%)" : "Nuevo costo"}
                value={formatInputWithSeparators(nuevoMonto, locale)}
                onChange={(e) => handleNumericInputChange(e, (val) => setNuevoMonto(val), locale)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">{tipoAjuste === "porcentaje" ? "%" : "$"}</InputAdornment>
                  ),
                }}
              />
            </Paper>
          </Grid>

          {/* Sección Margen */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: "100%", borderColor: "success.light", bgcolor: "rgba(76,175,80,0.02)" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "success.dark", mb: 2 }}>
                Margen de ganancia (opcional)
              </Typography>
              <TextField
                label="Margen %"
                value={formatInputWithSeparators(nuevoMargen, locale)}
                onChange={(e) => handleNumericInputChange(e, (val) => setNuevoMargen(val), locale)}
                fullWidth
                size="small"
                placeholder="Ej: 25"
                InputProps={{
                  startAdornment: <InputAdornment position="start">%</InputAdornment>,
                }}
                helperText="Dejar en blanco para no modificar"
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Sección precios de clientes */}
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              PRECIOS DE CLIENTES
            </Typography>
          </Divider>

          {!esReferente ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Este proveedor <strong>no es el referente</strong>. Los precios de clientes no se actualizarán
                a menos que lo promuevas como referente.
              </Alert>
              <FormControlLabel
                control={
                  <Switch checked={promoverReferente} onChange={(e) => handleTogglePromover(e.target.checked)} color="warning" size="small" />
                }
                label={<Typography variant="body2" fontWeight={500}>Promover a proveedor referente</Typography>}
                sx={{ mb: mostrarTransferList ? 2 : 0 }}
              />
            </>
          ) : (
            <FormControlLabel
              control={
                <Switch checked={actualizarPreciosClientes} onChange={(e) => handleToggleActualizarPrecios(e.target.checked)} color="primary" size="small" />
              }
              label={<Typography variant="body2" fontWeight={500}>Actualizar también precios de clientes</Typography>}
              sx={{ mb: mostrarTransferList ? 2 : 0 }}
            />
          )}

          {mostrarTransferList && (
            <Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 1 }}>
                <InfoOutlinedIcon fontSize="small" color="info" sx={{ mt: 0.2 }} />
                <Typography variant="caption" color="text.secondary">
                  Seleccioná los clientes cuyos precios querés actualizar.
                  {(!nuevoMargen || nuevoMargen === "") && (
                    <strong> Sin margen definido, los precios no se recalcularán.</strong>
                  )}
                </Typography>
              </Box>
              {loadingClientes ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <TransferList
                  disponibles={clientesDisponibles}
                  seleccionados={clientesSeleccionados}
                  onChange={(disp, sel) => {
                    setClientesDisponibles(disp);
                    setClientesSeleccionados(sel);
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleGuardar}
          disabled={saving || (nuevoMonto === "" || nuevoMonto == null) || (typeof nuevoMonto === "number" && isNaN(nuevoMonto))}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: "none" }}
        >
          {saving ? "Guardando..." : "Guardar cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalEditarPrecioId;
